import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ImageUploader from "@/components/ImageUploader";

const EMPTY = {
  title: "", property_type: "office", status: "available", price: "",
  size_sqft: "", location: "", description: "", images: [], featured: false,
};

function ProtectedGate({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (user === false) navigate("/login"); }, [user, navigate]);
  if (user === null) return <div className="min-h-screen flex items-center justify-center overline text-muted-foreground">Checking session…</div>;
  if (!user) return null;
  return children;
}

export default function Admin() {
  return <ProtectedGate><AdminInner /></ProtectedGate>;
}

function AdminInner() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/properties", { params: { status: "all" } }).then(({ data }) => setProperties(data));
    api.get("/inquiries").then(({ data }) => setInquiries(data)).catch(() => {});
  };
  useEffect(load, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ ...p, size_sqft: String(p.size_sqft), images: p.images || [] });
    setOpen(true);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      size_sqft: parseInt(form.size_sqft, 10) || 0,
      images: form.images,
    };
    try {
      if (editing) {
        await api.put(`/properties/${editing}`, payload);
        toast.success("Property updated");
      } else {
        await api.post("/properties", payload);
        toast.success("Property created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this property?")) return;
    await api.delete(`/properties/${id}`);
    toast.success("Property deleted");
    load();
  };

  const removeInquiry = async (id) => {
    await api.delete(`/inquiries/${id}`);
    load();
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  const inputCls = "rounded-none border-border h-11 focus-visible:ring-0 focus-visible:border-foreground";

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border sticky top-0 bg-background/90 backdrop-blur-xl z-40">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 h-16 flex items-center justify-between">
          <div className="font-display text-lg font-extrabold tracking-tight">RUBINO<span className="font-light"> Real Estate</span> <span className="overline text-muted-foreground ml-2">Dashboard</span></div>
          <div className="flex items-center gap-6">
            <span className="hidden sm:block overline text-muted-foreground">{user.email}</span>
            <button data-testid="logout-btn" onClick={doLogout} className="inline-flex items-center gap-2 overline link-underline"><LogOut size={15} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-12">
        <Tabs defaultValue="properties">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-10">
            <TabsList className="rounded-none bg-secondary/60 p-1 h-auto">
              <TabsTrigger data-testid="tab-properties" value="properties" className="rounded-none px-6 py-2 data-[state=active]:bg-foreground data-[state=active]:text-background">Properties · {properties.length}</TabsTrigger>
              <TabsTrigger data-testid="tab-inquiries" value="inquiries" className="rounded-none px-6 py-2 data-[state=active]:bg-foreground data-[state=active]:text-background">Enquiries · {inquiries.length}</TabsTrigger>
            </TabsList>
            <button data-testid="new-property-btn" onClick={openCreate} className="inline-flex items-center justify-center gap-2 border border-foreground px-6 py-3 overline hover-invert hover:bg-foreground hover:text-background">
              <Plus size={16} /> New property
            </button>
          </div>

          <TabsContent value="properties">
            <div data-testid="properties-table" className="border border-border divide-y divide-border">
              <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-secondary/40 overline text-muted-foreground">
                <span className="col-span-5">Property</span>
                <span className="col-span-2">Type</span>
                <span className="col-span-2">Status</span>
                <span className="col-span-2">Size</span>
                <span className="col-span-1 text-right">Actions</span>
              </div>
              {properties.map((p) => (
                <div key={p.id} data-testid={`admin-row-${p.id}`} className="grid grid-cols-1 md:grid-cols-12 gap-4 px-5 py-4 items-center hover:bg-secondary/30">
                  <div className="col-span-5 flex items-center gap-4">
                    <div className="h-12 w-16 bg-muted overflow-hidden shrink-0"><img src={p.images?.[0]} alt="" className="h-full w-full object-cover" /></div>
                    <div>
                      <div className="font-medium">{p.title}</div>
                      <div className="text-xs text-muted-foreground">{p.location}</div>
                    </div>
                  </div>
                  <div className="col-span-2 capitalize text-sm">{p.property_type}</div>
                  <div className="col-span-2"><span className="overline">{p.status}</span></div>
                  <div className="col-span-2 font-mono text-sm">{p.size_sqft.toLocaleString()} m²</div>
                  <div className="col-span-1 flex items-center justify-end gap-3">
                    <button data-testid={`edit-${p.id}`} onClick={() => openEdit(p)} className="hover:text-foreground text-muted-foreground"><Pencil size={17} /></button>
                    <button data-testid={`delete-${p.id}`} onClick={() => remove(p.id)} className="hover:text-destructive text-muted-foreground"><Trash2 size={17} /></button>
                  </div>
                </div>
              ))}
              {properties.length === 0 && <div className="px-5 py-10 text-muted-foreground text-sm">No properties yet.</div>}
            </div>
          </TabsContent>

          <TabsContent value="inquiries">
            <div data-testid="inquiries-list" className="space-y-4">
              {inquiries.map((q) => (
                <div key={q.id} data-testid={`inquiry-${q.id}`} className="border border-border p-6 flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-display text-xl">{q.name}</span>
                      {q.property_title && <span className="overline bg-secondary px-2 py-0.5">{q.property_title}</span>}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">{q.email}{q.phone ? ` · ${q.phone}` : ""}</div>
                    <p className="mt-3 text-sm max-w-2xl">{q.message}</p>
                    <div className="overline text-muted-foreground mt-3">{new Date(q.created_at).toLocaleString()}</div>
                  </div>
                  <button data-testid={`delete-inquiry-${q.id}`} onClick={() => removeInquiry(q.id)} className="text-muted-foreground hover:text-destructive self-start"><X size={18} /></button>
                </div>
              ))}
              {inquiries.length === 0 && <div className="text-muted-foreground text-sm py-10">No enquiries yet.</div>}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl tracking-tight">{editing ? "Edit property" : "New property"}</DialogTitle></DialogHeader>
          <form onSubmit={save} data-testid="property-form" className="space-y-5 mt-2">
            <div>
              <label className="overline text-muted-foreground">Title</label>
              <Input data-testid="form-title" required value={form.title} onChange={set("title")} className={inputCls + " mt-2"} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="overline text-muted-foreground">Type</label>
                <Select value={form.property_type} onValueChange={set("property_type")}>
                  <SelectTrigger data-testid="form-type" className={inputCls + " mt-2"}><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="hotel">Hotel</SelectItem>
                    <SelectItem value="office">Office</SelectItem>
                    <SelectItem value="retail">Retail</SelectItem>
                    <SelectItem value="industrial">Industrial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="overline text-muted-foreground">Status</label>
                <Select value={form.status} onValueChange={set("status")}>
                  <SelectTrigger data-testid="form-status" className={inputCls + " mt-2"}><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="leased">Leased</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="overline text-muted-foreground">Price</label>
                <Input data-testid="form-price" required value={form.price} onChange={set("price")} placeholder="$48 / sqft / yr" className={inputCls + " mt-2"} />
              </div>
              <div>
                <label className="overline text-muted-foreground">Size (m²)</label>
                <Input data-testid="form-size" required type="number" value={form.size_sqft} onChange={set("size_sqft")} className={inputCls + " mt-2"} />
              </div>
            </div>
            <div>
              <label className="overline text-muted-foreground">Location</label>
              <Input data-testid="form-location" required value={form.location} onChange={set("location")} className={inputCls + " mt-2"} />
            </div>
            <div>
              <label className="overline text-muted-foreground">Description</label>
              <Textarea data-testid="form-description" required rows={4} value={form.description} onChange={set("description")} className="rounded-none border-border focus-visible:ring-0 focus-visible:border-foreground mt-2 resize-none" />
            </div>
            <div>
              <label className="overline text-muted-foreground">Photos <span className="text-muted-foreground/60">(first image is the cover)</span></label>
              <div className="mt-3">
                <ImageUploader value={form.images} onChange={(imgs) => setForm((f) => ({ ...f, images: imgs }))} />
              </div>
            </div>
            <button data-testid="form-submit" disabled={saving} className="w-full border border-foreground py-3.5 overline hover-invert hover:bg-foreground hover:text-background disabled:opacity-50">
              {saving ? "Saving…" : editing ? "Update property" : "Create property"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
